import {
  createContext,
  useContext,
  createSignal,
  createEffect,
  on,
} from 'solid-js';
import { unwrap } from 'solid-js/store';
import { spec, specTrack } from 'framework';
import { schemaValidator } from 'schemas';

const LayerContext = createContext({});
const useLayers = () => useContext(LayerContext);

interface LayerInstance {
  id: string;
  linked?: string;
  exclusiveOf?: string[];
}

type LayerInstanceMap = Record<string, LayerInstance>;

const getRootLayerId = (
  instances: LayerInstanceMap,
  id: string,
  visited: Set<string> = new Set(),
): string => {
  if (visited.has(id)) {
    console.warn(
      `Cycle detected in linked layers: ${[...visited, id].join(' -> ')}`,
    );
    return id;
  }
  const layer = instances[id];
  if (layer?.linked && instances[layer.linked]) {
    visited.add(id);
    return getRootLayerId(instances, layer.linked, visited);
  }
  return id;
};

const getLinkedGroup = (
  instances: LayerInstanceMap,
  rootId: string,
): Set<string> => {
  const group = new Set<string>([rootId]);
  for (const [id, layer] of Object.entries(instances)) {
    if (layer.linked && getRootLayerId(instances, id) === rootId) {
      group.add(id);
    }
  }
  return group;
};

const allowsLayer = (
  instances: LayerInstanceMap,
  layerAId: string,
  layerBId: string,
): boolean => {
  const layerA = instances[layerAId];
  if (!layerA) return false;

  if (
    layerA.linked &&
    getRootLayerId(instances, layerAId) === getRootLayerId(instances, layerBId)
  ) {
    return true;
  }
  const layerB = instances[layerBId];
  if (
    layerB?.linked &&
    getRootLayerId(instances, layerBId) === getRootLayerId(instances, layerAId)
  ) {
    return true;
  }

  if (!layerA.exclusiveOf || layerA.exclusiveOf.length === 0) {
    return true;
  }

  const rootB = getRootLayerId(instances, layerBId);
  return (
    !layerA.exclusiveOf.includes(layerBId) &&
    !layerA.exclusiveOf.includes(rootB)
  );
};

const areCompatible = (
  instances: LayerInstanceMap,
  idA: string,
  idB: string,
): boolean => {
  if (idA === idB) return true;
  if (getRootLayerId(instances, idA) === getRootLayerId(instances, idB))
    return true;
  return allowsLayer(instances, idA, idB) && allowsLayer(instances, idB, idA);
};

export const resolveVisibleLayers = (
  instances: LayerInstanceMap,
  requestedIds: string[],
): string[] => {
  const requestedRoots = requestedIds.filter(
    (id) => instances[id] && !instances[id].linked,
  );

  const visibleRoots = new Set<string>();

  for (const id of requestedRoots) {
    const incompatible = Array.from(visibleRoots).filter(
      (existingId) => !areCompatible(instances, id, existingId),
    );
    incompatible.forEach((incompatibleId) =>
      visibleRoots.delete(incompatibleId),
    );
    visibleRoots.add(id);
  }

  const finalVisible = new Set<string>();
  for (const rootId of visibleRoots) {
    getLinkedGroup(instances, rootId).forEach((id) => finalVisible.add(id));
  }

  return Array.from(finalVisible);
};

const LayerProvider = (props) => {
  const [layerInstances, setLayerInstances] = createSignal({});
  const [legendLayers, setLegendLayers] = createSignal([]);
  const [activeLayer, setActiveLayer] = createSignal(null);
  const [visibleLayers, _setVisibleLayers] = createSignal([]);

  createEffect(
    on(
      () => specTrack(props.layers),
      () => {
        if (!props.layers) return;
        let isFetching = true;
        const layerLoader = async (lyrspec) => {
          const layerType = lyrspec.layerSpec.layerType;
          const layermod = await import(
            `../../components/worldlayers/${layerType}/src/index.ts`
          );
          const schema = await import(
            `../../components/worldlayers/${layerType}/schemas/layerspec.json`
          );
          const validate = schemaValidator.compile(schema);
          if (validate(lyrspec.layerSpec)) {
            console.log('Loaded layer ' + lyrspec.id);
            return new layermod.default(lyrspec);
          } else {
            validate.errors.map((error) =>
              console.log(
                `Spec for layer ${lyrspec.id} does not validate: ${error.instancePath} -> ${error.message} [${JSON.stringify(error.params)}]`,
              ),
            );
            return null;
          }
        };
        const layerloads = props.layers.map((spec) =>
          layerLoader(unwrap(spec)),
        );
        const newLayerInstances = {};
        const newVisibleLayers = [];
        const newLegendLayers = [];
        Promise.all(layerloads).then((loaded) => {
          loaded.map((lyrinstance) => {
            if (lyrinstance) {
              newLayerInstances[lyrinstance.id] = lyrinstance;
              if (lyrinstance.visible) newVisibleLayers.push(lyrinstance.id);
              if (lyrinstance.legend) newLegendLayers.push(lyrinstance);
            }
          });
          for (const lyr of Object.values(newLayerInstances)) {
            if (lyr.linked && !newLayerInstances[lyr.linked]) {
              console.warn(
                `Layer "${lyr.id}" declares linked layer "${lyr.linked}" which does not exist`,
              );
            }
          }
          setLayerInstances(newLayerInstances);
          setLegendLayers(newLegendLayers);
          setVisibleLayers(newVisibleLayers);
        });
        return () => {
          isFetching = false;
        };
      },
    ),
  );

  const setVisibleLayers = (requestedVisibleIdsArg) => {
    const requestedIds = [...unwrap(requestedVisibleIdsArg)];
    _setVisibleLayers(resolveVisibleLayers(layerInstances(), requestedIds));
  };

  const zoomVisibility = (id, zoom) => {
    const layer = layerInstances()[id];
    const hidden = zoom < layer.minZoom || zoom > layer.maxZoom;
    return !hidden;
  };

  return (
    <LayerContext.Provider
      value={{
        layerInstances,
        legendLayers,
        activeLayer,
        setActiveLayer,
        visibleLayers,
        setVisibleLayers,
        zoomVisibility,
      }}
    >
      {props.children}
    </LayerContext.Provider>
  );
};

export { useLayers, LayerProvider };
